import React, { useState, Fragment } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Modal, Button, Alert } from 'antd';
import classes from './ReconnectGCPCloudAccount.module.css';
import ScanRunningInstances from '../../CredentialsForm/ScanRunningInstances';
import TestConnectionContent from '../ConfigurationTab/Components/TestConnectionContent';
import { connect } from 'dva';
import { encryptAES, formatRoleNames } from '@/utils/utils';
import { AdvanceInfoStepsByShell, BasicInfoStepsByShell } from './GCPInfoSteps';
import CryptoJS from 'crypto-js';

const InfoSteps = ({
  protectionLevel,
  props,
  handleFileChange,
  handleBeforeUpload,
  handleInputChange,
  roleNames,
}) => {
  if (protectionLevel === 'basic') {
    return (
      <BasicInfoStepsByShell
        props={props}
        handleFileChange={handleFileChange}
        handleBeforeUpload={handleBeforeUpload}
      />
    );
  }
  if (protectionLevel === 'advanced') {
    return (
      <AdvanceInfoStepsByShell
        props={props}
        handleFileChange={handleFileChange}
        handleBeforeUpload={handleBeforeUpload}
        handleInputChange={handleInputChange}
        roleNames={roleNames}
      />
    );
  }
};

const ReconnectGCPCloudAccount = props => {
  const {
    scanRunningInstances,
    cloudAccountData,
    testConnectionTypes,
    allCloudAccountsList,
    userName,
    updating,
    onClose,
    isForceFullyReconnecting,
    updateProtecionLevelTo = cloudAccountData.protectionLevel || 'basic',
  } = props;
  const { protectionLevel } = props.cloudAccountData;
  const [showConnectionTesting, setShowConnectionTesting] = useState(false);
  const [updatingAccount, setUpdatingAccount] = useState(false);
  const [serviceAccount, setServiceAccount] = useState('');
  const [roleNames, setRoleNames] = useState({
    cloudnosysCreateRole: '',
    cloudnosysDeleteRole: '',
  });

  const isAdvancedAcc = updateProtecionLevelTo.toLowerCase() === 'advanced';

  const handleInputChange = (field, event) => {
    const value = event.target.value;
    setRoleNames(prevRoleNames => ({
      ...prevRoleNames,
      [field]: value,
    }));
  };

  const handleFileChange = info => {
    const { file, fileList } = info;

    if (file.status === 'done') {
      message.success(`${file.name} file uploaded successfully.`);
      // Handle file upload success, possibly inform parent component
    } else if (file.status === 'error') {
      message.error(`${file.name} file upload failed.`);
      // Handle file upload failure, possibly inform parent component
    }

    // Check if a file was removed from the list
    if (
      fileList.length === 0 ||
      fileList.find(f => f.uid === file.uid && file.status === 'removed')
    ) {
      // File has been removed, perform necessary state update or inform parent
      setServiceAccount(''); // Example: reset the related form value
    }
  };

  const handleBeforeUpload = file => {
    const reader = new FileReader();
    reader.onload = e => {
      // Here you can access the file content
      const content = e.target.result;
      try {
        setServiceAccount(content);
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    };
    reader.readAsText(file);

    return false;
  };

  const handleFinishReconnectAndScan = async () => {
    const payload = {
      ...cloudAccountData,
    };

    const { provider, orgId, id: cloudAccountId } = cloudAccountData;
    const { dispatch, userName } = props;

    // ** update the cloud account data
    await dispatch({
      type: 'cloudAccount/update',
      payload: {
        params: {
          type: 'edit',
          provider,
          orgId,
          id: cloudAccountId,
        },
        data: {
          protectionLevel,
          updatedAt: new Date(),
          updatedBy: userName,
          isActive: true,
        },
      },
    });

    // ** start scan
    runScan(payload);

    // ** close modal
    onClose();
  };

  const runScan = accountData => {
    const { dispatch } = props;

    // Get currently scanning cloud accounts Ids
    let currentlyScanningCloudAccountsIds = getCurrentlyScanningCloudAccountsIds({
      shouldFilterWithStartScanStatus: true,
    });

    // Reset scan progress if currently no cloud account is scanning
    if (currentlyScanningCloudAccountsIds.length === 0) {
      dispatch({
        type: 'cloudAccount/clearScanProgress',
      });
    }

    dispatch({
      type: 'scan/runScan',
      payload: {
        cloudAccounts: [accountData],
        orgId: accountData.orgId,
        currentlyScanningCloudAccountsIds,
      },
    });

    dispatch({
      type: 'scopeReport/current',
    });
  };

  const getCurrentlyScanningCloudAccountsIds = ({ shouldFilterWithStartScanStatus = true }) => {
    const { allCloudAccountsList } = props;

    // Get currently scanning cloud accounts Ids
    const currentlyScanningCloudAccountsIds = allCloudAccountsList
      .filter(
        cloudAccount =>
          cloudAccount.status === 'running-scan' ||
          (shouldFilterWithStartScanStatus && cloudAccount.status === 'start-scan')
      )
      .map(cloudAccount => cloudAccount.id);

    return currentlyScanningCloudAccountsIds;
  };

  const isAllConnectionConnected = () =>
    testConnectionTypes &&
    Object.keys(testConnectionTypes).every(key => testConnectionTypes[key].status === 200);

  const updateAccountAndTestConnection = async () => {
    setUpdatingAccount(true);
    const { dispatch } = props;

    let credentials = JSON.parse(serviceAccount);
    let projectId = credentials.project_id;

    let googlKey = { projectId, credentials };
    let userkeys = CryptoJS.AES.encrypt(JSON.stringify(googlKey), REACT_APP_SECRET).toString();

    const payloadData = {
      cloudXrayConfig: {
        ...cloudAccountData.cloudXrayConfig,
        scanRunningInstances,
        updatedAt: new Date(),
      },
      userkeys,
      roleNames: formatRoleNames(roleNames),
    };
    await dispatch({
      type: 'cloudAccount/save',
      payload: {
        roleNames: formatRoleNames(roleNames),
      },
    });
    await dispatch({
      type: 'cloudAccount/update',
      payload: {
        params: {
          type: 'edit',
          provider: cloudAccountData.provider,
          orgId: cloudAccountData.orgId,
          id: cloudAccountData.id,
        },
        data: payloadData,
      },
    });

    setUpdatingAccount(false);
    setShowConnectionTesting(true);
  };

  return (
    <>
      {!showConnectionTesting && (
        <>
          <Alert
            type="warning"
            showIcon
            message="Remove the identity (service account + IAM) named “cloudnosys-sa-****” from your GCP project <projectID>, and the respective key “cloudnosys-sa*.json”before reconnecting"
            style={{
              marginBottom: '1rem',
            }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h5 className={classes.stepsHeadings}>
              <span>Create Service Account</span>
            </h5>
          </div>

          <InfoSteps
            protectionLevel={protectionLevel}
            props={props}
            handleFileChange={handleFileChange}
            handleBeforeUpload={handleBeforeUpload}
            handleInputChange={handleInputChange}
            roleNames={roleNames}
          />
          {protectionLevel === 'advanced' && <ScanRunningInstances />}

          <span
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: '10px',
            }}
          >
            <Button
              type="primary"
              htmlType="button"
              loading={updatingAccount}
              onClick={() => {
                updateAccountAndTestConnection();
              }}
              disabled={!serviceAccount}
            >
              Test Connection
            </Button>
          </span>
        </>
      )}

      {showConnectionTesting && (
        <>
          <TestConnectionContent
            cloudAccountData={props.cloudAccountData}
            byPassAndTestAll={true}
            isEditCloudAccount={isForceFullyReconnecting ? false : true}
            connectionProtectionType={isAdvancedAcc ? 'advanced' : 'basic'}
            roleNames={roleNames}
          />

          <Button
            type="primary"
            style={{ float: 'right' }}
            disabled={!isAllConnectionConnected()}
            onClick={handleFinishReconnectAndScan}
            loading={props.updating}
          >
            Finish Reconnect & Scan
          </Button>
        </>
      )}
    </>
  );
};

export default connect(({ user, cloudAccount }) => ({
  updating: cloudAccount.updating,
  scanRunningInstances: cloudAccount.scanRunningInstances,
  testConnectionTypes: cloudAccount.testConnectionTypes,
  allCloudAccountsList: cloudAccount.allCloudAccountsList,
  gcpSignedURL: cloudAccount.gcpSignedURL,
  loadingSignedUrl: cloudAccount.loadingSignedUrl,
  userName: user.currentUser.name,
}))(ReconnectGCPCloudAccount);
